import { useEffect, useRef, useState } from "react";
import { fetchJson } from "../lib/fetchJson";
import {
  INITIAL_LEAD_FILTERS,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from "../lib/constants";
import {
  buildPagination,
  buildGeographyData,
  getCityOptionsForRegion,
  getRootDispositions,
  userHasNamedTabAccess,
} from "../lib/utils";
import { CITY_REGION_MAP } from "../components/leads/LeadFilters";
import {
  readStoredLeadFilters,
  readStoredLeadSearch,
  readStoredLeadPageSize,
  clearStoredLeadFilters,
  persistLeadFilters,
  LEAD_PAGE_SIZE_KEY,
} from "../lib/storage";

const AUTO_REFRESH_MS = 60_000;
const CHANGE_POLL_MS = 5_000;

function isFormFieldFocused() {
  if (typeof document === "undefined") {
    return false;
  }

  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }

  const tagName = activeElement.tagName;
  return (
    activeElement.isContentEditable ||
    activeElement.getAttribute("role") === "textbox" ||
    ["INPUT", "SELECT", "TEXTAREA"].includes(tagName)
  );
}

export function useLeads({
  currentUser,
  contextReady,
  context,
  roleScope,
  activeSidebarItem,
  activeTopNav,
  // modal open states — needed for change-poll guard
  leadModalOpen = false,
  journeyModalOpen = false,
  bulkModalOpen = false,
  journeyPickerOpen = false,
  guideOpen = false,
  duplicateStateOpen = false,
  // report filters — needed for auto-refresh dep array
  reportFilters = {},
  setError,
}) {
  const [leadResponse, setLeadResponse] = useState({
    count: 0,
    next: null,
    previous: null,
    results: [],
  });
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState(() => readStoredLeadSearch());
  const [filters, setFilters] = useState(() => readStoredLeadFilters());
  const [leadPageSize, setLeadPageSize] = useState(() => readStoredLeadPageSize());
  const [followUps, setFollowUps] = useState([]);
  const [summary, setSummary] = useState({ followUpsCount: 0 });
  const [assignableAgents, setAssignableAgents] = useState([]);
  const [loading, setLoading] = useState({
    workspace: false,
    detail: false,
    submittingLead: false,
    submittingCall: false,
    submittingBulk: false,
    syncingFacebook: false,
    assignableAgents: false,
    uploadingDocuments: false,
    exportingReport: false,
    updatingAvailability: false,
  });

  // Proper 400 ms debounce
  const [deferredSearch, setDeferredSearch] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setDeferredSearch(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  const autoRefreshInFlightRef = useRef(false);
  const bulkUploadingRef = useRef(false);
  const workspaceVersionRef = useRef("");
  const dispositionPopupRef = useRef(null);

  // Derived
  const agents = (context?.users || []).filter(
    (user) =>
      user.role === "agent" && userHasNamedTabAccess(user, "sales_crm", "sales crm"),
  );
  const geographyData = buildGeographyData(
    context?.geographies || [],
    context?.available_regions || [],
    context?.available_cities || [],
  );
  const rootDispositions = getRootDispositions(context?.disposition_groups || []);
  const totalPages = Math.max(Math.ceil(leadResponse.count / leadPageSize), 1);
  const paginationItems = buildPagination(totalPages, filters.page);

  useEffect(() => {
    setAssignableAgents(agents);
  }, [context?.users]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LEAD_PAGE_SIZE_KEY, String(leadPageSize));
  }, [leadPageSize]);

  // Persist filters + search to localStorage
  useEffect(() => {
    persistLeadFilters(filters, search);
  }, [filters, search]);

  async function loadWorkspaceVersion() {
    return fetchJson("/api/crm/changes/");
  }

  async function fetchLeadDetail(leadId) {
    if (!leadId) return null;
    return fetchJson(`/api/crm/leads/${leadId}/`);
  }

  async function refreshSelectedLead(leadId, options = {}) {
    if (!leadId) {
      setSelectedLead(null);
      return null;
    }
    if (!options.silent) {
      setLoading((current) => ({ ...current, detail: true }));
    }
    const payload = await fetchLeadDetail(leadId);
    setSelectedLead(payload);
    if (!options.silent) {
      setLoading((current) => ({ ...current, detail: false }));
    }
    return payload;
  }

  async function loadWorkspace(options = {}) {
    if (!currentUser) return;

    if (!options.silent) {
      setLoading((current) => ({ ...current, workspace: true }));
    }

    const params = new URLSearchParams({
      page: String(filters.page),
      page_size: String(leadPageSize),
    });

    const stripStubs = (csv) =>
      (csv || "")
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v && !v.startsWith("__no_"))
        .join(",");

    if (deferredSearch) {
      params.set("search", deferredSearch);
    }
    if (filters.registeredDate) {
      params.set("registered_date", filters.registeredDate);
    }
    if (filters.registeredDateEnd) {
      params.set("registered_date_end", filters.registeredDateEnd);
    }
    const sourceCsv = stripStubs(filters.sourceId);
    const sourceHadStubOnly = filters.sourceId && !sourceCsv;
    if (sourceCsv) {
      params.set("source", sourceCsv);
    }
    if (filters.region) {
      const regionParts = filters.region
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean);
      const hasOtherCities = regionParts.includes("Other Cities");
      const realRegions = regionParts.filter((r) => r !== "Other Cities");
      if (realRegions.length > 0) {
        params.set("region", realRegions.join(","));
      }
      if (hasOtherCities) {
        const otherCityNames = geographyData.otherCities || [];
        const existingCities = filters.city
          ? filters.city
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean)
          : [];
        const mergedCities = Array.from(new Set([...existingCities, ...otherCityNames]));
        if (mergedCities.length > 0) {
          params.set("city", mergedCities.join(","));
        }
      } else if (filters.city) {
        params.set("city", filters.city);
      }
    } else if (filters.city) {
      params.set("city", filters.city);
    }
    const selectedDispositionId = filters.subDispositionId || filters.dispositionRootId;
    if (selectedDispositionId) {
      params.set("disposition", selectedDispositionId);
    }
    if (filters.status) {
      params.set("status", filters.status);
    }
    if (filters.customerStatus) {
      params.set("customer_status", filters.customerStatus);
    }
    if (filters.followUpBucket) {
      params.set("follow_up_bucket", filters.followUpBucket);
    }
    if (filters.timeLeft) {
      params.set("time_left", filters.timeLeft);
    }
    if (filters.attemptedCalls) {
      params.set("attempted_calls_bucket", filters.attemptedCalls);
    }
    if (filters.connectedCalls) {
      params.set("connected_calls_bucket", filters.connectedCalls);
    }
    if (filters.ordering) {
      params.set("ordering", filters.ordering);
    }

    if (roleScope?.can_see_franchise && filters.showFranchise)
      params.set("show_franchise", "1");
    if (roleScope?.can_see_franchise && filters.campaignType)
      params.set("campaign_type", filters.campaignType);

    if (roleScope?.scope === "own_leads") {
      params.set("assigned_to", String(currentUser.id));
    } else if (filters.assignedToId) {
      params.set("assigned_to", filters.assignedToId);
    }

    const isOnFollowUpsTab = activeSidebarItem === "Follow-ups";
    const isOnReportOrDashboard = ["Reports", "Dashboard"].includes(activeSidebarItem);
    const followUpParams = new URLSearchParams({ status: "pending" });
    if (roleScope?.scope === "own_leads") {
      followUpParams.set("assigned_to", String(currentUser.id));
    } else if (filters.assignedToId) {
      followUpParams.set("assigned_to", filters.assignedToId);
    }

    const [leadPayload, followUpPayload] = await Promise.all([
      sourceHadStubOnly
        ? Promise.resolve({ results: [], count: 0, next: null, previous: null })
        : fetchJson(`/api/crm/leads/?${params.toString()}`),
      isOnFollowUpsTab
        ? fetchJson(`/api/crm/follow-ups/?${followUpParams.toString()}`)
        : isOnReportOrDashboard
        ? fetchJson(`/api/crm/follow-ups/count/?${followUpParams.toString()}`)
        : Promise.resolve(null),
    ]);

    setLeadResponse(leadPayload);
    if (isOnFollowUpsTab && followUpPayload !== null) {
      setFollowUps(followUpPayload);
      setSummary({ followUpsCount: followUpPayload.length });
    } else if (isOnReportOrDashboard && followUpPayload !== null) {
      setSummary({ followUpsCount: followUpPayload.count ?? 0 });
    }
    if (!options.silent) {
      setLoading((current) => ({ ...current, workspace: false }));
    }
  }

  function handleLeadFilterChange(field, value) {
    setFilters((current) => {
      const next = { ...current, [field]: value, page: 1 };

      if (field === "dispositionRootId") {
        next.subDispositionId = "";
      }

      if (field === "region") {
        const selectedRegions = value
          ? value
              .split(",")
              .map((r) => r.trim())
              .filter(Boolean)
          : [];
        if (selectedRegions.length > 0) {
          const allowedCities = new Set(
            Object.entries(CITY_REGION_MAP)
              .filter(([, r]) => selectedRegions.includes(r))
              .map(([c]) => c.trim().toLowerCase()),
          );
          const currentCities = current.city
            ? current.city
                .split(",")
                .map((c) => c.trim())
                .filter(Boolean)
            : [];
          const validCities = currentCities.filter((c) =>
            allowedCities.has(c.toLowerCase()),
          );
          next.city = validCities.join(",");
        }
        // Do not clear the city when region is cleared, to allow 'Others' to be selected
      }

      return next;
    });
  }

  function clearLeadFilters() {
    setFilters(INITIAL_LEAD_FILTERS);
    setSearch("");
    clearStoredLeadFilters();
  }

  // Auto-refresh interval (60s) + visibility change
  useEffect(() => {
    if (!currentUser || !contextReady) return;

    async function refreshDynamicData() {
      if (
        autoRefreshInFlightRef.current ||
        bulkUploadingRef.current ||
        document.visibilityState === "hidden"
      ) {
        return;
      }

      autoRefreshInFlightRef.current = true;
      try {
        const jobs = [];

        if (activeTopNav === "Sales") {
          jobs.push(loadWorkspace({ silent: true }));
        }

        if (selectedLeadId && !leadModalOpen && !journeyModalOpen) {
          jobs.push(refreshSelectedLead(selectedLeadId, { silent: true }));
        }

        const results = await Promise.allSettled(jobs);
        const failed = results.find((result) => result.status === "rejected");
        if (failed?.reason?.isNetworkError) {
          setError(failed.reason.message);
        }
      } finally {
        autoRefreshInFlightRef.current = false;
      }
    }

    const id = setInterval(refreshDynamicData, AUTO_REFRESH_MS);

    function handleVisibility() {
      if (document.visibilityState === "visible") {
        refreshDynamicData();
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [
    activeSidebarItem,
    activeTopNav,
    contextReady,
    currentUser,
    deferredSearch,
    filters.assignedToId,
    filters.attemptedCalls,
    filters.campaignType,
    filters.city,
    filters.connectedCalls,
    filters.customerStatus,
    filters.dispositionRootId,
    filters.followUpBucket,
    filters.ordering,
    filters.page,
    filters.registeredDate,
    filters.registeredDateEnd,
    filters.region,
    filters.showFranchise,
    filters.sourceId,
    filters.status,
    filters.subDispositionId,
    filters.timeLeft,
    leadPageSize,
    reportFilters.assignedToId,
    reportFilters.period,
    reportFilters.region,
    reportFilters.sourceId,
    roleScope?.scope,
    leadModalOpen,
    journeyModalOpen,
    selectedLeadId,
  ]);

  // Change-poll interval (5s)
  useEffect(() => {
    if (!currentUser || !contextReady) return;

    let active = true;
    let inFlight = false;

    async function refreshWhenChanged() {
      if (
        inFlight ||
        bulkUploadingRef.current ||
        document.visibilityState === "hidden"
      ) {
        return;
      }
      if (activeTopNav !== "Sales") {
        return;
      }

      const dispositionPopupOpen = Boolean(
        dispositionPopupRef.current && !dispositionPopupRef.current.closed,
      );
      const userIsEditing =
        leadModalOpen ||
        journeyModalOpen ||
        bulkModalOpen ||
        journeyPickerOpen ||
        guideOpen ||
        duplicateStateOpen ||
        dispositionPopupOpen ||
        isFormFieldFocused();
      if (userIsEditing) {
        return;
      }

      inFlight = true;
      try {
        const payload = await loadWorkspaceVersion();
        if (!active) return;

        const nextVersion = payload?.workspace_version || "";
        if (!workspaceVersionRef.current) {
          workspaceVersionRef.current = nextVersion;
          return;
        }
        if (!nextVersion || nextVersion === workspaceVersionRef.current) {
          return;
        }

        workspaceVersionRef.current = nextVersion;
        const jobs =
          activeSidebarItem === "My Agents" ? [] : [loadWorkspace({ silent: true })];
        if (selectedLeadId && !leadModalOpen && !journeyModalOpen) {
          jobs.push(refreshSelectedLead(selectedLeadId, { silent: true }));
        }
        if (!jobs.length) return;
        await Promise.allSettled(jobs);
      } catch (requestError) {
        if (active && requestError?.isNetworkError) {
          setError(requestError.message);
        }
      } finally {
        inFlight = false;
      }
    }

    refreshWhenChanged();
    const id = setInterval(refreshWhenChanged, CHANGE_POLL_MS);

    return () => {
      active = false;
      clearInterval(id);
    };
  }, [
    activeSidebarItem,
    activeTopNav,
    bulkModalOpen,
    contextReady,
    currentUser,
    deferredSearch,
    filters.assignedToId,
    filters.attemptedCalls,
    filters.campaignType,
    filters.city,
    filters.connectedCalls,
    filters.customerStatus,
    filters.dispositionRootId,
    filters.followUpBucket,
    filters.ordering,
    filters.page,
    filters.registeredDate,
    filters.registeredDateEnd,
    filters.region,
    filters.showFranchise,
    filters.sourceId,
    filters.status,
    filters.subDispositionId,
    filters.timeLeft,
    duplicateStateOpen,
    guideOpen,
    leadPageSize,
    leadModalOpen,
    journeyModalOpen,
    journeyPickerOpen,
    reportFilters.assignedToId,
    reportFilters.period,
    reportFilters.region,
    reportFilters.sourceId,
    roleScope?.scope,
    selectedLeadId,
  ]);

  // Primary workspace sync (filter changes)
  useEffect(() => {
    if (!currentUser || !contextReady) return;
    if (activeTopNav === "Sales" && activeSidebarItem === "My Agents") {
      setLoading((current) => ({ ...current, workspace: false }));
      return;
    }

    async function syncWorkspace() {
      try {
        setError("");
        await loadWorkspace();
      } catch (requestError) {
        setError(requestError.message);
        setLoading((current) => ({ ...current, workspace: false }));
      }
    }

    syncWorkspace();
  }, [
    activeSidebarItem,
    activeTopNav,
    contextReady,
    currentUser,
    deferredSearch,
    filters.assignedToId,
    filters.attemptedCalls,
    filters.campaignType,
    filters.city,
    filters.connectedCalls,
    filters.customerStatus,
    filters.dispositionRootId,
    filters.followUpBucket,
    filters.ordering,
    filters.page,
    filters.registeredDate,
    filters.registeredDateEnd,
    filters.region,
    filters.showFranchise,
    filters.sourceId,
    filters.status,
    filters.subDispositionId,
    filters.timeLeft,
    leadPageSize,
    roleScope?.scope,
  ]);

  // Auto-select first lead when list changes
  useEffect(() => {
    if (!leadResponse.results.length) {
      setSelectedLeadId(null);
      setSelectedLead(null);
      return;
    }

    const selectedStillVisible = leadResponse.results.some(
      (lead) => String(lead.id) === String(selectedLeadId),
    );
    if (!selectedLeadId || !selectedStillVisible) {
      setSelectedLeadId(String(leadResponse.results[0].id));
    }
  }, [leadResponse.results, selectedLeadId]);

  // Sync selected lead detail when selection changes
  useEffect(() => {
    if (!selectedLeadId || !currentUser) return;
    if (activeTopNav === "Sales" && activeSidebarItem === "My Agents") return;

    async function syncLeadDetail() {
      try {
        setError("");
        await refreshSelectedLead(selectedLeadId);
      } catch (requestError) {
        setError(requestError.message);
        setLoading((current) => ({ ...current, detail: false }));
      }
    }

    syncLeadDetail();
  }, [activeSidebarItem, activeTopNav, selectedLeadId, currentUser]);

  function resetLeads() {
    setLeadResponse({ count: 0, next: null, previous: null, results: [] });
    setFollowUps([]);
    setSelectedLeadId(null);
    setSelectedLead(null);
    setFilters(INITIAL_LEAD_FILTERS);
    setSearch("");
    setSummary({ followUpsCount: 0 });
    setAssignableAgents([]);
  }

  return {
    agents,
    assignableAgents,
    bulkUploadingRef,
    clearLeadFilters,
    deferredSearch,
    dispositionPopupRef,
    fetchLeadDetail,
    filters,
    followUps,
    geographyData,
    handleLeadFilterChange,
    leadPageSize,
    leadResponse,
    loading,
    paginationItems,
    refreshSelectedLead,
    resetLeads,
    rootDispositions,
    search,
    selectedLead,
    selectedLeadId,
    setAssignableAgents,
    setError,
    setFilters,
    setFollowUps,
    setLeadPageSize,
    setLeadResponse,
    setLoading,
    setSearch,
    setSelectedLead,
    setSelectedLeadId,
    setSummary,
    summary,
    totalPages,
    loadWorkspace,
  };
}
